const MAX_FRAME_MS = 32;
const RESPONSE_MS = 32;
const SETTLED_LOG = 0.001;
export const ZOOM_IDLE_MS = 180;

/** Trackpad pinches track fingers directly; Shift-wheel retains its eased scale. */
export function wheelZoomAmount(event, viewportHeight) {
  if (!event.shiftKey && !event.ctrlKey) return 0;
  const dx = Number.isFinite(event.deltaX) ? event.deltaX : 0;
  const dy = Number.isFinite(event.deltaY) ? event.deltaY : 0;
  const pageHeight = Number.isFinite(viewportHeight) && viewportHeight > 0 ? viewportHeight : 1;
  const units = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? pageHeight : 1;
  // Browsers encode native trackpad pinch scale in deltaY. Horizontal gesture
  // noise must not change its direction, and normal deltas must not be clipped.
  if (event.ctrlKey) return Math.max(-4, Math.min(4, dy * units * 0.01));
  const delta = Math.abs(dy) > Math.abs(dx) ? dy : dx;
  const amount = -delta * units * 0.003;
  return Math.max(-4, Math.min(4, amount));
}

function prefersReducedMotion() {
  return typeof window !== 'undefined'
    && Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
}

/**
 * Apply logarithmic zoom independently of expensive rendering: ease mouse wheel
 * input, or follow direct trackpad input immediately with no animated tail.
 * The caller applies each step around the same anchor for the entire burst.
 */
export function createZoomMotion({
  onStep,
  onStart = () => {},
  onEnd = () => {},
  requestFrame = callback => requestAnimationFrame(callback),
  cancelFrame = id => cancelAnimationFrame(id),
  now = () => performance.now(),
  reducedMotion = prefersReducedMotion,
}) {
  let active = false;
  let remaining = 0;
  let anchor = null;
  let frame = null;
  let lastTime = null;
  let generation = 0;
  let mode = null;
  let idleTimer = null;

  function stopScheduledWork() {
    if (frame !== null) cancelFrame(frame);
    if (idleTimer !== null) clearTimeout(idleTimer);
    frame = null;
    idleTimer = null;
    generation++;
  }

  function reset() {
    stopScheduledWork();
    active = false;
    remaining = 0;
    anchor = null;
    lastTime = null;
    mode = null;
  }

  function finish() {
    reset();
    onEnd();
  }

  function finishAfterIdle() {
    if (idleTimer !== null) clearTimeout(idleTimer);
    const token = generation;
    idleTimer = setTimeout(() => {
      if (token !== generation || !active) return;
      idleTimer = null;
      finish();
    }, ZOOM_IDLE_MS);
  }

  function schedule() {
    if (frame !== null || !active) return;
    const token = generation;
    frame = requestFrame(timestamp => {
      if (token !== generation || !active) return;
      frame = null;
      // RAF timestamps describe the start of a rendering tick. Input may have
      // arrived later in that tick, so use the callback's clock as a lower bound.
      const time = Math.max(Number.isFinite(timestamp) ? timestamp : -Infinity, now());
      const dt = Math.max(0, Math.min(MAX_FRAME_MS, time - lastTime));
      lastTime = time;
      let step = remaining * -Math.expm1(-dt / RESPONSE_MS);
      if (Math.abs(remaining - step) <= SETTLED_LOG) step = remaining;
      remaining -= step;
      if (step) onStep(step, anchor, { inFrame: true });
      // Consumers may cancel or add more input from onStep.
      if (token !== generation || !active) return;
      if (remaining === 0) finish();
      else schedule();
    });
  }

  return {
    push(logAmount, nextAnchor, { direct = false } = {}) {
      if (!Number.isFinite(logAmount) || logAmount === 0) return;
      const nextMode = direct ? 'direct' : 'eased';
      if (mode !== nextMode) {
        // A new input source must not inherit old motion. Keep the burst's
        // anchor and lifecycle so changing sources cannot jump the image.
        stopScheduledWork();
        remaining = 0;
        lastTime = null;
        mode = nextMode;
      }
      if (!direct) {
        if (Math.sign(logAmount) !== Math.sign(remaining)) {
          remaining = 0;
          lastTime = now();
        }
        // Keep every same-direction wheel delta. Exponential response consumes
        // larger bursts faster without discarding travel or imposing a speed cap.
        remaining += logAmount;
      }
      const token = generation;
      if (!active) {
        active = true;
        // Preserve the location even if the caller reuses a mutable point object.
        anchor = Array.isArray(nextAnchor) ? [...nextAnchor]
          : nextAnchor && typeof nextAnchor === 'object' ? { ...nextAnchor } : nextAnchor;
        onStart();
      }
      if (token !== generation || !active) return;
      if (direct) {
        // Only lifecycle completion is delayed. Every camera step happens in
        // the input event, while renderers coalesce paints on their own RAF.
        finishAfterIdle();
        onStep(logAmount, anchor);
        return;
      }
      const instant = typeof reducedMotion === 'function' ? reducedMotion() : reducedMotion;
      if (instant) {
        if (frame !== null) cancelFrame(frame);
        frame = null;
        const step = remaining;
        remaining = 0;
        onStep(step, anchor);
        if (token === generation && active) finish();
      } else schedule();
    },
    cancel() {
      reset();
    },
    snapshot() {
      if (!active || !remaining) return null;
      return {
        amount: remaining,
        anchor: Array.isArray(anchor) ? [...anchor]
          : anchor && typeof anchor === 'object' ? { ...anchor } : anchor,
      };
    },
    isActive() { return active; },
  };
}
